import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom'
import { useTranslations } from '@/core/translation'
import { roundOf, ROUTES, CURRENCIES, usePackageInfo } from '@/utils'
import { usePriceUtils } from '@/components/Data'
import { useAppSelector } from '@/redux/core'
import { selectIsAuthenticated } from '@/redux/auth'
import { selectCurrency } from '@/redux/app'
import { useUserExperiences } from '@/core/userExperience'

export function usePackageSummaryController() {
  const t = useTranslations('reviewPackage')
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const { value: userCurrency } = useAppSelector(selectCurrency)
  const navigate = useNavigate()
  const location = useLocation()
  const { currentPackage, productCount } = usePackageInfo()
  const { getFormattedPrice } = usePriceUtils()
  const { isDirectCheckoutEnabled } = useUserExperiences()

  const onContinueClick = useCallback(() => {
    navigate({
      pathname:
        isDirectCheckoutEnabled || isAuthenticated
          ? ROUTES.PACKAGE.CHECKOUT
          : ROUTES.PACKAGE.CONTACT_INFO,
      search: createSearchParams({ sessionId: currentPackage?.sessionId ?? '' }).toString(),
    })
  }, [currentPackage?.sessionId, isDirectCheckoutEnabled, navigate, isAuthenticated])

  const dueAtProperty = useMemo(
    () =>
      (currentPackage?.hotelInfo?.roomAndRateInfo.additionalCharges?.reduce(
        (sum, item) => sum + item.amount,
        0,
      ) ?? 0) * (currentPackage?.hotelParams?.rooms.length ?? 0),
    [
      currentPackage?.hotelInfo?.roomAndRateInfo.additionalCharges,
      currentPackage?.hotelParams?.rooms.length,
    ],
  )

  const savings = useMemo(
    () => roundOf(currentPackage?.cartInfo.pricing?.total?.savings ?? 0, 0),
    [currentPackage],
  )

  const packageCurrency = currentPackage?.cartInfo.currency ?? CURRENCIES.USD
  const isAtCheckout = location.pathname === ROUTES.PACKAGE.CHECKOUT
  const showActualCharge = isAtCheckout && userCurrency !== packageCurrency
  const actualChargeFormatted = showActualCharge
    ? getFormattedPrice({
        value: currentPackage?.cartInfo.pricing?.total?.total ?? 0,
        precision: 2,
        currency: packageCurrency,
      })
    : ''

  return {
    t,
    userCurrency,
    currentPackage,
    productCount,
    dueAtProperty,
    savings,
    packageCurrency,
    showActualCharge,
    actualChargeFormatted,
    onContinueClick,
  }
}
